import type { AnyRouter } from '@tanstack/react-router';

/**
 * Holds the client-side TanStack Router instance so that code running outside
 * of React (zustand stores, plain utilities, API layer) can perform SPA
 * navigation instead of a full-page `window.location` reload.
 *
 * The instance is set once after hydration from the root route's `useEffect`
 * (client only), so there is no cross-request leakage during SSR.
 */
let routerRef: AnyRouter | null = null;

export function setRouterInstance(router: AnyRouter): void {
  routerRef = router;
}

/**
 * Navigate to an already-resolved path (e.g. `/en/auth/authorize`) from
 * non-React code. Uses the router for an SPA transition when available, and
 * falls back to `window.location` if called before the router is registered
 * (e.g. very early during bootstrap).
 */
export function navigateToPath(
  path: string,
  options?: { replace?: boolean }
): void {
  if (routerRef) {
    // Use history push/replace so a full href (pathname + query string) is
    // parsed into the router location; `navigate({ to })` would not parse an
    // embedded `?query`.
    if (options?.replace) {
      routerRef.history.replace(path);
    } else {
      routerRef.history.push(path);
    }
    return;
  }

  if (typeof window !== 'undefined') {
    if (options?.replace) {
      window.location.replace(path);
    } else {
      window.location.assign(path);
    }
  }
}
